<script setup>
import {
    computed, nextTick, onMounted, ref, watch,
} from "vue";
import ImageUpload from "@/components/ImageUpload";
import AgesSelect from "@/components/AgesSelect";
import RadioInputGroup from "@/components/forms/RadioInputGroup.vue";
import SelectInput from "@/components/Select/SelectInput.vue";
import userMixin from "../mixins/userMixins";

const emit = defineEmits(["updated", "setImg"]);
const props = defineProps({
    default: {
        type: Object,
    },
    // eslint-disable-next-line vue/require-prop-type-constructor
    setActivityData: false,
    errors: {
        eventType: "",
        ages: "",
        name: "",
        img: "",
        description: "",
        primaryCategory: "",
        secondaryCategories: "",
        bookingEmail: "",
        bookingType: "",
        bookingLink: "",
    },
    defaultCategories: {
        type: Array,
        default: () => [],
    },
    reset: {
        type: Number,
        default: 0,
    },
});

const activity = ref({
    eventType: "",
    ages: [],
    name: "",
    img: "",
    description: "",
    primaryCategory: [],
    secondaryCategories: [],
    bookingEmail: "",
    bookingType: "",
    bookingLink: "",
});

const isSuperAdmin = ref(null);

const availableTypes = ref([
    { name: "Class", value: 3160 },
    { name: "Activity", value: 3165 },
    { name: "Workshop", value: 3164 },
    { name: "Holiday Camp", value: 3166 },
    { name: "Performance", value: 3167 },
    { name: "Exhibition", value: 3168 },
    { name: "Event", value: 3161 },
]);

function setImage(img) {
    emit("setImg", img);
}

function resetData() {
    activity.value = {
        eventType: "",
        ages: [],
        name: "",
        img: "",
        description: "",
        primaryCategory: [],
        secondaryCategories: [],
        bookingEmail: "",
        bookingType: "",
        bookingLink: "",
    };
}

function emitUpdate() {
    emit("updated", activity.value);
}

const descriptionCharLeft = computed(() => 3000 - (activity.value?.description?.length || 0));

const categories = computed(() => (
    (window.allCategories !== null && window.allCategories !== undefined)
        ? window.allCategories
        : props.defaultCategories
));

const objectExistAndNotEmpty = (obj) => {
    if (obj === null || obj === undefined || typeof obj !== "object") {
        return false;
    }

    return Object.values(obj).filter(
        (el) => (
            el !== null
            && el !== undefined
            && el !== ""
            && el.length !== 0
        ),
    ).length > 0;
};

watch(
    () => activity.value,
    // eslint-disable-next-line no-unused-vars
    (newvalue, old) => {
        emitUpdate();
    },
    { deep: true },
);

watch(
    () => props.reset,
    // eslint-disable-next-line no-unused-vars
    (newvalue, old) => resetData(),
);

onMounted(async () => {
    const isDefaultDataNotEmpty = objectExistAndNotEmpty(props.default);
    nextTick(() => {
        if (isDefaultDataNotEmpty) {
            activity.value = props.default;
        }
    });

    isSuperAdmin.value = await userMixin.methods.isSuperAdmin();
});

function getIntialDefaultActivityType(eventType) {
    const typeValue = availableTypes.value.filter((type) => type.name === eventType);
    return typeValue[0] || null;
}

// eslint-disable-next-line no-unused-vars
watch(
    () => props.setActivityData,
    () => {
        activity.value = props.setActivityData;
        if (objectExistAndNotEmpty(props.default)) {
            activity.value = {
                ...activity.value,
                ...props.default,
            };
        }
        activity.value.eventType = getIntialDefaultActivityType(props.setActivityData.eventType);
    },
);
</script>
<template>
    <form>
        <div class="d-flex flex-row justify-content-between event-type-and-age-container">
            <div class="d-flex flex-column select-container select-container-width" data-test="event-type">
                <label class="show">Event type<span class="required-field">*</span></label>
                <SelectInput
                    :error="errors?.eventType"
                    label="name"
                    dataTest="event-type-select"
                    :options="availableTypes"
                    :default="getIntialDefaultActivityType(
                        activity.eventType?.name || activity.eventType
                    )"
                    @change="activity.eventType = $event"
                    placeholder="Choose an event type"
                    id="type-of-event"
                    data-test-id="man-pan-type-category-dropdown"
                    class="with-underline"
                />
            </div>

            <div class="select-container-width age-select-container" data-test="event-ages">
                <label class="show">Suitable ages<span class="required-field">*</span></label>
                <AgesSelect
                    :error="errors?.ages"
                    class="mt-2"
                    label-text="Ages"
                    dataTest="event-ages-select"
                    key="edit-listing-details"
                    :hide-label="true"
                    :default="activity.ages"
                    @update="(e) => (activity.ages = e)"
                    :class="{ 'error-input-border-rounded': errors?.ages }"
                />
            </div>
        </div>

        <div class="d-flex flex-column mt-3" data-test="event-name">
            <label class="show">
                {{ activity.eventType?.name || "Event" }} name<span class="required-field">*</span>
            </label>
            <input
                type="text"
                v-model="activity.name"
                placeholder="Enter an event name"
                data-test="event-name-input"
                id="activity-name"
                class="text mt-2"
                maxlength="45"
                :class="{ 'error-input-border-rounded': errors?.name }"
                data-test-id="man-pan-input-event-name"
            />
            <div v-if="errors?.name">
                <div class="error-text" data-test="validation-error">{{ errors?.name }}</div>
            </div>
        </div>

        <div v-if="isSuperAdmin" class="d-flex flex-column mt-3" data-test="affiliated-link">
            <label class="show">
                Affiliated link
            </label>
            <input
                type="text"
                v-model="activity.bookingLink"
                placeholder="Enter an affiliated link"
                data-test="affiliated-link-input"
                id="affiliated-link"
                class="text mt-2"
                maxlength="255"
                :class="{ 'error-input-border-rounded': errors?.bookingLink }"
                data-test-id="man-pan-input-event-name"
            />
            <div v-if="errors?.bookingLink">
                <div class="error-text" data-test="validation-error">{{ errors?.bookingLink }}</div>
            </div>
        </div>

        <div class="mt-3 d-flex flex-column" data-test="event-desc">
            <label class="show">
                {{ activity.eventType?.name || "Event" }}
                description<span class="required-field">*</span>
            </label>
            <textarea
                v-model="activity.description"
                maxlength="3000"
                class="mt-2"
                data-test="event-desc-textarea"
                id="description"
                :class="{ 'error-input-border-rounded': errors?.description }"
                placeholder="Enter an event description"
                rows="3"
                data-test-id="man-pan-input-event-desc"
            ></textarea>
            <div class="information-text">{{ descriptionCharLeft }} characters left</div>
        </div>

        <div data-test="event-picture">
            <ImageUpload
                @cropped="setImage($event)"
                :main-img="activity.img"
                dataTest="event-picture-upload"
                :block-title="(activity.eventType?.name || 'Event') + ' picture'"
                :block-class="(
                    errors?.img !== ''
                    && typeof errors?.img !== 'undefined'
                )
                    ? 'error-input-border-rounded'
                    : ''
                "
            />
            <div v-if="errors?.img">
                <div class="error-text" data-test="validation-error">{{ errors?.img }}</div>
            </div>
        </div>

        <div class="d-flex flex-row justify-content-between top-gap main-sub-category-container">
            <div class="select-container select-container-width" data-test="event-category-primary">
                <label class="show">Main category<span class="required-field">*</span></label>
                <SelectInput
                    :error="errors?.primaryCategory"
                    :options="categories"
                    label="name"
                    dataTest="event-category-primary-select"
                    class="select-category-container mt-2"
                    :default="activity.primaryCategory"
                    @change="activity.primaryCategory = $event"
                    placeholder="Choose a category"
                    id="main-categories"
                    data-test-id="man-pan-main-category-dropdown"
                />
            </div>

            <div class="select-container select-container-width other-categories-container"
                 data-test="event-category-secondary">
                <label class="show">Other category (optional)</label>
                <SelectInput
                    :options="categories"
                    label="name"
                    dataTest="event-category-secondary-select"
                    class="select-category-container mt-2"
                    :default="activity.secondaryCategories"
                    @change="activity.secondaryCategories = $event"
                    placeholder="Choose a category"
                    id="secondary-category"
                />
            </div>
        </div>

        <div data-test="event-email">
            <label class="mt-3 show">Booking email<span class="required-field">*</span></label>
            <input
                v-model="activity.bookingEmail"
                type="email"
                data-test="event-email-input"
                placeholder="Enter an email"
                class="text w-100 mt-2"
                :class="{ 'error-input-border-rounded': errors?.bookingEmail }"
            />
            <div v-if="errors?.bookingEmail">
                <div class="error-text" data-test="validation-error">{{ errors?.bookingEmail }}</div>
            </div>
        </div>

        <div data-test="event-attendance">
            <label class="mt-3 show">Attendance<span class="required-field">*</span></label>
            <div class="d-flex flex-column my-1 mt-2">
                <RadioInputGroup
                    label="title"
                    v-model="activity.bookingType"
                    dataTest="event-attendance-radio"
                    :selected="activity.bookingType"
                    :list="[
                        {
                            title: 'Booking is required',
                            value: 'required',
                            dataTest: 'radio-attendance-required',
                        },
                        {
                            title: 'Drop in',
                            value: 'drop-in',
                            dataTest: 'radio-attendance-dropin',
                        },
                    ]"
                />
            </div>
            <div v-if="errors?.bookingType">
                <div class="error-text" data-test="validation-error">{{ errors?.bookingType }}</div>
            </div>
        </div>
    </form>
</template>

<style scoped>
label {
    margin-bottom: 0;
}

.top-gap {
    margin-top: 1rem;
}

.select-container-width {
    width: 20.875rem !important;
}

.information-text {
    color: #999999;
}

.error-input-border-rounded {
    border: 1px solid #fb0100 !important;
    border-radius: 10px;
}

label {
    font-size: 0.75rem;
}

@media only screen and (max-width: 768px) {
    .event-type-and-age-container, .main-sub-category-container {
        flex-wrap: wrap;
    }

    .age-select-container, .other-categories-container {
        margin-top: 1rem;
    }
}
</style>
